/**
 * Box-select and double-click select. A left press on bare ground (the tile
 * handler reports it, so a press on a unit or building never starts one)
 * that travels past the drag threshold draws a marquee (`ui.marquee`) and,
 * on release, selects the own units drawn inside it (shift adds; otherwise
 * it clears the building selection and tile inspection like a ground click).
 * A double-click on an own unit selects every own unit of its type on screen
 * (shift adds). Neither runs while a placement or targeting mode is armed.
 * The camera pans on right and middle drag instead (`StrategyCamera`).
 */
import { type RefObject, useCallback, useEffect, useRef } from 'react';
import type * as THREE from 'three';
import type { FrameStore } from '@/game/view/frame';
import { type Pickable, UNIT_CENTER_Y } from '../picking';
import {
  exceedsDrag,
  idsInRect,
  idsOfType,
  isDoubleClick,
  normaliseRect,
  type Point,
  projectToScreen,
  type ScreenUnit,
  type UnitClick,
  unionIds,
} from '../ui/boxSelect';
import type { UiStore } from '../ui/store';

export function useBoxSelect({
  ui,
  frames,
  areaRef,
  cameraRef,
  selectOnMap,
  onGroundSelect,
}: {
  ui: UiStore;
  frames: FrameStore;
  /** The element the canvas fills: the screen rectangle projections map to. */
  areaRef: RefObject<HTMLElement | null>;
  cameraRef: RefObject<THREE.Camera | null>;
  /** The plain click on an own unit (selects just it). */
  selectOnMap: (id: string, additive: boolean) => void;
  /** A box-select replaced the selection: clear what a ground click clears besides it. */
  onGroundSelect: () => void;
}) {
  // Where each drawn unit is now, kept up to date by UnitsLayer.
  const drawnRef = useRef<readonly Pickable[]>([]);
  const startRef = useRef<Point | null>(null);
  const lastClickRef = useRef<UnitClick | null>(null);

  /** Own drawn units projected to client pixels. */
  const onScreen = useCallback((): ScreenUnit[] => {
    const camera = cameraRef.current;
    const area = areaRef.current?.getBoundingClientRect();
    if (!camera || !area) return [];
    const { view, playerId } = frames.get();
    const own = drawnRef.current.filter((p) => view.units[p.id]?.owner === playerId);
    return projectToScreen(camera, own, area, UNIT_CENTER_Y);
  }, [cameraRef, areaRef, frames]);

  const select = useCallback(
    (ids: readonly string[], additive: boolean) =>
      ui.set((s) => ({
        selectedUnitIds: additive ? unionIds(s.selectedUnitIds, ids) : ids,
      })),
    [ui]
  );

  /** A left press on the ground: may turn into a marquee. */
  const onGroundDown = useCallback(
    (e: PointerEvent) => {
      const { placementMode, targetMode } = ui.get();
      if (e.button !== 0 || placementMode || targetMode) return;
      lastClickRef.current = null;
      startRef.current = { x: e.clientX, y: e.clientY };
    },
    [ui]
  );

  useEffect(() => {
    const cancel = () => {
      startRef.current = null;
      ui.set({ marquee: null });
    };
    const onMove = (e: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      // Released somewhere we heard nothing of.
      if ((e.buttons & 1) === 0) return cancel();
      const here = { x: e.clientX, y: e.clientY };
      if (exceedsDrag(start, here)) ui.set({ marquee: normaliseRect(start, here) });
    };
    const onUp = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const start = startRef.current;
      cancel();
      if (!start) return;
      const here = { x: e.clientX, y: e.clientY };
      if (!exceedsDrag(start, here)) return;
      // A mode armed mid-drag takes the release.
      const { placementMode, targetMode } = ui.get();
      if (placementMode || targetMode) return;
      select(idsInRect(onScreen(), normaliseRect(start, here)), e.shiftKey);
      if (!e.shiftKey) {
        ui.set({ selectedBuildingId: null });
        onGroundSelect();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [ui, onScreen, select, onGroundSelect]);

  /** A left-click on an own unit: select it, or on a double-click all of its type in view. */
  const onSelectUnit = useCallback(
    (id: string, additive: boolean) => {
      const now = performance.now();
      const { view, playerId } = frames.get();
      const unit = view.units[id];
      const double = isDoubleClick(lastClickRef.current, id, now);
      lastClickRef.current = double ? null : { id, time: now };
      const type = unit?.owner === playerId ? unit.type : undefined;
      if (!double || !type) {
        selectOnMap(id, additive);
        return;
      }
      const ids = idsOfType(
        onScreen().map((u) => u.id),
        (uid) => view.units[uid]?.type,
        type
      );
      select(ids, additive);
    },
    [frames, onScreen, select, selectOnMap]
  );

  return { drawnRef, onGroundDown, onSelectUnit };
}
